/**
 * Validation for location cleaning standards (PUT and PATCH draft).
 * Used by locationCleaningStandardsService mock and real.
 */

const VALID_DEVICES = ['V50', 'MS50', 'A50'];
const VALID_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function validateLocationStandardsBody(body) {
  const windows = body?.windows;
  if (!Array.isArray(windows) || windows.length === 0 || windows.length > 5) {
    throw new Error('windows must be an array with 1 to 5 items');
  }
  const names = new Set();
  for (let i = 0; i < windows.length; i++) {
    const w = windows[i];
    if (!w.name || typeof w.name !== 'string') {
      throw new Error(`Window at index ${i}: name is required`);
    }
    if (names.has(w.name)) {
      throw new Error(`Window names must be unique within the location: duplicate "${w.name}"`);
    }
    names.add(w.name);
    const days = w.days;
    if (!Array.isArray(days) || days.length === 0) {
      throw new Error(`Window "${w.name}": days must include at least 1 day`);
    }
    for (const d of days) {
      if (!VALID_DAYS.includes(String(d).toLowerCase())) {
        throw new Error(`Window "${w.name}": invalid day "${d}"`);
      }
    }
    const sessionType = w.sessionType;
    if (sessionType !== 'single' && sessionType !== 'multiple') {
      throw new Error(`Window "${w.name}": sessionType must be "single" or "multiple"`);
    }
    const sessions = w.sessions || [];
    if (sessionType === 'single' && sessions.length !== 1) {
      throw new Error(`Window "${w.name}": sessionType "single" requires exactly 1 session`);
    }
    if (sessionType === 'multiple' && (sessions.length < 2 || sessions.length > 5)) {
      throw new Error(`Window "${w.name}": sessionType "multiple" requires 2–5 sessions`);
    }
    for (const s of sessions) {
      if (!s.primaryRange?.from || !s.primaryRange?.to) {
        throw new Error(`Window "${w.name}": each session must have primaryRange.from and primaryRange.to`);
      }
      const alts = s.alternativeRanges || [];
      if (alts.length > 2) {
        throw new Error(`Window "${w.name}": at most 2 alternativeRanges per session`);
      }
    }
    const processes = w.processes || [];
    if (processes.length === 0) {
      throw new Error(`Window "${w.name}": at least 1 process is required`);
    }
    const devicesInWindow = new Set();
    for (const p of processes) {
      const device = p.assignedDevice;
      if (!VALID_DEVICES.includes(device)) {
        throw new Error(`Window "${w.name}": invalid assignedDevice "${device}"`);
      }
      if (devicesInWindow.has(device)) {
        throw new Error(`Window "${w.name}": each device can appear only once (duplicate ${device})`);
      }
      devicesInWindow.add(device);
      const ts = p.timeStandard;
      if (ts == null || typeof ts.minimum !== 'number' || typeof ts.maximum !== 'number') {
        throw new Error(`Window "${w.name}": process must have timeStandard.minimum and timeStandard.maximum`);
      }
      if (ts.minimum < 1 || ts.maximum < ts.minimum) {
        throw new Error(`Window "${w.name}": timeStandard.minimum >= 1 and timeStandard.maximum >= minimum`);
      }
      if (device === 'MS50') {
        const mc = p.ms50Config;
        if (!mc?.moppingDuration || mc.rinseAndWringCycles == null) {
          throw new Error(`Window "${w.name}": MS50 process requires ms50Config with moppingDuration and rinseAndWringCycles`);
        }
        if (mc.rinseAndWringCycles <= 0) {
          throw new Error(`Window "${w.name}": ms50Config.rinseAndWringCycles must be > 0`);
        }
      }
    }
    if (w.otherTasksTime != null) {
      const { minimum, maximum } = w.otherTasksTime;
      if (typeof minimum !== 'number' || typeof maximum !== 'number' || minimum < 0 || maximum < minimum) {
        throw new Error(`Window "${w.name}": otherTasksTime must have minimum >= 0 and maximum >= minimum`);
      }
    }
  }
}

export function validateDraftBody(body) {
  const windows = body?.windows;
  if (!Array.isArray(windows) || windows.length > 5) {
    throw new Error('draft windows must be an array with 0 to 5 items');
  }
  for (let i = 0; i < windows.length; i++) {
    const w = windows[i];
    if (!w || typeof w !== 'object') {
      throw new Error(`draft window at index ${i} must be an object`);
    }
    const name = w.name != null ? String(w.name) : 'Window';
    if (w.days && !Array.isArray(w.days)) {
      throw new Error(`draft window "${name}": days must be an array`);
    }
    if (w.sessions != null && !Array.isArray(w.sessions)) {
      throw new Error(`draft window "${name}": sessions must be an array`);
    }
    if (w.processes != null && !Array.isArray(w.processes)) {
      throw new Error(`draft window "${name}": processes must be an array`);
    }
  }
}
